import Enterprise from '@common-assets/enterprise.png';
import { BillingPeriod } from "@common-components/subscription-selection/subscription-selection";
import { CircularProgress } from "@mui/material";
import { activeSubscriptionSelector, selectUserObject, selectIsDarkMode } from "@common-reducers/UserSelectors";
import { useGetPlanMapMutation, useGetItemPricesMutation, useGetEntitlementsMutation } from "@common-reducers/backend-api/backend-api";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { AnalyticsService } from "@common-services/analytics-service";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { namespace } from "@common-config/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlanDescriptor, appConfig } from "@common/config/app.config";
import { ButtonType } from "../bluesnap-button/bluesnap-button";
import ChargebeeButton from '@common-components/chargebee-button/chargebee-button';
import { shouldShowFreeTileCancelCta } from './cancel-cta';
import { resolvePlanCta, PlanCta } from './plan-cta';
import { useRouter } from "@common-reducers/hooks/use-router";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, X, Crown, Sparkles, Star, Zap, Users, Calendar, Shield, TrendingUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// Plan icon mapping
const planIcons = {
    Free: Star,
    Scheduler: Zap,
    Manager: Sparkles,
    Campaigner: Crown,
};

export interface PlanSelectionProps {

}

interface PaymentPlan {
    best?: boolean;
    name: string;
    planKey: string;
    key: string;
    limitations: any[];
    benefits: any[];
    common: any[];
    upgrade?: boolean;
    enterprise?: boolean;
    tip?: string;
    price?: number;
    period?: string;
}

const freeLimitsMap: Record<string, string[]> = {
    "unlimited-messages": ["planSelection.freeLimitMessages"],
    "unlimited-tasks": ["planSelection.freeLimitTasks"],
    "offline-socket": ["planSelection.freeLimitOffline"]
};

const entitlementDescriptionMap: Record<string, string> = {
    "unlimited-messages": "planSelection.entitlementMessages",
    "unlimited-tasks": "planSelection.entitlementTasks",
    "remove-branding": "planSelection.entitlementBranding",
    "offline-socket": "planSelection.entitlementOffline"
};

const featureOrder = [
    "remove-branding",
    "offline-socket",
    "unlimited-messages",
    "unlimited-tasks",
];

export const PlanSelection = () => {
    const isDarkMode = useAppSelector(selectIsDarkMode);
    const { t } = useTranslation(namespace);
    const analyticsService: AnalyticsService = useAnalyticsService();
    const router = useRouter();
    const urlCouponCode = (router.query.coupon || router.query.discount) as string | undefined;
    const user = useAppSelector(selectUserObject);
    const activeSubscription = useAppSelector(activeSubscriptionSelector);
    const isGettingPaymentUrl = useAppSelector(state => state.PaymentFormReducer.isGettingPaymentUrl);
    const [getPlanMap, { isLoading: isLoadingPlanMap, data: planMap }] = useGetPlanMapMutation();
    const [getItemPrices, { isLoading: isLoadingPrices, data: itemPricesData }] = useGetItemPricesMutation();
    const [getEntitlements, { data: entitlementsData }] = useGetEntitlementsMutation();

    const [_planDuration, setPlanDuration] = useState<BillingPeriod | undefined>();
    const [_seatCount, setSeatCount] = useState<number | undefined>();

    useEffect(() => {
        if (user) {
            console.log('Fetching plan data for user:', user);
            getPlanMap().unwrap()
                .then(response => console.log('Plan map response:', response))
                .catch(error => console.error('Error fetching plan map:', error));

            getItemPrices().unwrap()
                .then(response => console.log('Item prices response:', response))
                .catch(error => console.error('Error fetching item prices:', error));

            getEntitlements().unwrap()
                .then(response => console.log('Entitlements response:', response))
                .catch(error => console.error('Error fetching entitlements:', error));
        }
    }, [getPlanMap, getItemPrices, getEntitlements, user]);

    const planDuration = useMemo(() => _planDuration ?? activeSubscription?.billingPeriod ?? 'year', [_planDuration, activeSubscription?.billingPeriod])
    const monthlyBilling = useMemo(() => planDuration === 'month', [planDuration]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPlanDuration(event.target.checked ? 'month' : 'year');
    };

    const extensionPricing = useMemo(() => {
        const result = t('planSelection.extensionPricing', { returnObjects: true });
        return Array.isArray(result) ? result : [];
    }, [t]);

    const taskPricing = useMemo(() => {
        const result = t('planSelection.taskPricing', { returnObjects: true });
        return Array.isArray(result) ? result : [];
    }, [t]);

    const seatOptions: number[] = useMemo(() => Object.keys(planMap ?? {})
        .map(planName => (planMap ?? {})[planName])
        .filter((plan): plan is PlanDescriptor => !!plan && (plan as PlanDescriptor).intervalUnit === planDuration)
        .reduce<number[]>((_seatOptions, plan) => [
            ...new Set([..._seatOptions, plan.seatCount as number])
        ], [])
        .filter((x: number) => !!x)
        .sort((a: number, b: number) => a - b)
        , [planMap, planDuration]);

    const seatCount = useMemo(() => _seatCount ?? activeSubscription?.seatCount ?? seatOptions[0] ?? 1,
        [_seatCount, activeSubscription?.seatCount, seatOptions]);

    const entitlementsMap = useMemo(() => entitlementsData || {}, [entitlementsData]);


    const planDescription: PaymentPlan[] = useMemo(() => {
        const basePlans = appConfig.pricingOptions === 'extension' ? extensionPricing : taskPricing;

        console.log('itemPricesData:', itemPricesData);
        console.log('isLoadingPrices:', isLoadingPrices);
        console.log('planDuration:', planDuration);

        // If still loading or no data yet, return plans with 0 prices
        if (isLoadingPrices || !itemPricesData) {
            return (basePlans as PaymentPlan[]).map((plan: PaymentPlan) => ({
                ...plan,
                price: 0,
                period: planDuration
            }));
        }

        return (basePlans as PaymentPlan[]).map((plan: PaymentPlan) => {
            console.log('Processing plan:', plan);

            // Map the plan key to Chargebee plan type
            const planTypeMap: Record<string, string> = {
                'Free': 'Free',
                'Scheduler': 'Basic',
                'Manager': 'StandardNew',
                'Campaigner': 'Pro'
            };

            const chargebeePlanType = planTypeMap[plan.key] || plan.key;
            const chargebeePlan = ((itemPricesData as unknown) as Array<{
                id: string;
                name: string;
                item_family_id: string;
                item_id: string;
                status: string;
                tiers: Array<{
                    starting_unit: number;
                    ending_unit?: number;
                    price: number;
                }>;
            }>).find(price => {
                console.log('Checking price:', price);
                console.log('Matching against planType:', chargebeePlanType);
                console.log('Matching against periodUnit:', planDuration);

                // Extract plan type from Chargebee item ID (e.g., "Basic-USD-Monthly")
                const [planType] = price.id.split('-');
                return planType === chargebeePlanType &&
                    (planDuration === 'month' ? price.id.includes('Monthly') : price.id.includes('Yearly'));
            });

            console.log('Found chargebeePlan:', chargebeePlan);

            // Find the correct tier based on seatCount
            const tier = chargebeePlan?.tiers?.find(tier =>
                seatCount >= tier.starting_unit &&
                (!tier.ending_unit || seatCount <= tier.ending_unit)
            );

            console.log('Found tier:', tier);
            const price = tier?.price ?? 0;
            console.log('Final price:', price);

            // Use entitlements from API directly, mapping feature_id to translation
            const entitlementsArr: Array<{ feature_id: string }> = (entitlementsMap as Record<string, Array<{ feature_id: string }>>)[plan.planKey] || [];
            const benefits = featureOrder
                .map(feature => entitlementsArr.find((ent) => ent.feature_id === feature))
                .filter((ent): ent is { feature_id: string } => !!ent)
                .map(ent => t(entitlementDescriptionMap[ent.feature_id]));

            // Limitations: features in freeLimitsMap that are NOT in entitlementsArr
            const limitations = Object.keys(freeLimitsMap)
                .filter(key => !entitlementsArr.some((ent) => ent.feature_id === key))
                .map(key => t(freeLimitsMap[key]));

            return {
                ...plan,
                benefits,
                limitations,
                price: Number(Number(price / 100 / (chargebeePlan?.id.includes('Yearly') ? 12 : 1)).toFixed(1)),
                period: chargebeePlan ? (chargebeePlan.id.includes('Monthly') ? 'month' : 'year') : planDuration,
                best: chargebeePlanType === 'StandardNew'
            };
        });
    }, [extensionPricing, taskPricing, itemPricesData, isLoadingPrices, planDuration, entitlementsMap, seatCount, t]);

    const activePlanMap: Record<string, Record<string, PlanDescriptor>> = useMemo(() => Object.keys(planMap ?? {})
        .map(planId => ({
            ...(planMap ?? {})[planId],
            id: planId
        }))
        .filter(plan => plan.seatCount === seatCount && plan.intervalUnit === planDuration)
        .reduce<Record<string, Record<string, PlanDescriptor>>>((activePlans, plan) => {
            const planType = plan.planType ?? '';
            const bluesnapType = plan.bluesnapType ?? '';
            return {
                ...activePlans,
                [planType]: {
                    ...(activePlans[planType] ?? {}),
                    [bluesnapType]: plan as PlanDescriptor
                }
            };
        }, {})
        , [planMap, seatCount, planDuration]);


    const subscriptionBox = (index: number, paymentPlanData: PaymentPlan, isFullWidth: boolean = false) => {
        const planAndContract: Record<string, PlanDescriptor> = activePlanMap[paymentPlanData.key];
        const plan = planAndContract?.plan;

        const hasPaidSubscription = (activeSubscription?.order ?? 0) > 0;

        // FE#156 — decide Upgrade/Current/Update/Downgrade via the pure comparator.
        // It compares `order` first (seats/interval only break ties within an order)
        // and NEVER coerces a missing `order` into a downgrade, so a Free user can no
        // longer be shown "Downgrade"/"Current" on a paid tile and blocked from buying.
        const ctaDecision = resolvePlanCta(
            {
                order: plan?.order,
                seatCount: plan?.seatCount,
                intervalUnit: plan?.intervalUnit,
                isFreePlan: paymentPlanData.key === 'Free',
            },
            activeSubscription
        );
        const isCurrentPlan = ctaDecision.isCurrentPlan;
        const upgrade = ctaDecision.isUpgrade;

        // Hero Logic:
        // If has paid sub -> Current Plan is Hero
        // Else -> Most Popular is Hero
        const isHero = hasPaidSubscription ? isCurrentPlan : paymentPlanData.best;

        // Most Popular Badge Logic:
        // Only show if NO paid sub AND is best
        const showMostPopularBadge = !hasPaidSubscription && paymentPlanData.best;

        const ctaLabelKey: Record<PlanCta, string> = {
            upgrade: 'planSelection.upgrade',
            current: 'planSelection.currentPlan',
            update: 'planSelection.update',
            downgrade: 'planSelection.downgrade',
        };
        const ctaButtonType: Record<PlanCta, ButtonType> = {
            upgrade: ButtonType.Upgrade,
            current: ButtonType.Current,
            update: ButtonType.Current,
            downgrade: ButtonType.Downgrade,
        };
        const CTA: string = t(ctaLabelKey[ctaDecision.cta]);
        const buttonType: ButtonType = ctaButtonType[ctaDecision.cta];

        const PlanIcon = planIcons[paymentPlanData.key as keyof typeof planIcons] || Star;

        // Free plan horizontal layout
        if (isFullWidth && paymentPlanData.key === 'Free') {
            return (
                <div
                    key={index}
                    className={cn(
                        "mt-8 p-6 rounded-xl border border-gray-200 dark:border-[#334155] bg-card dark:bg-[#1e293b] shadow-sm transition-all hover:shadow-md",
                        "flex flex-col md:flex-row items-center justify-between gap-6"
                    )}
                    data-testid={`subscription-plan-${paymentPlanData.name}`}
                >
                    {/* Left: Icon, Plan Name & Price */}
                    <div
                        className="flex items-center gap-5 flex-shrink-0 min-w-[200px]"
                    >
                        <div className="p-3 bg-blue-50 dark:bg-[#0f172a] rounded-xl">
                            <PlanIcon className="w-8 h-8 text-[#53BDEB]" />
                        </div>
                        <div className="text-left">
                            <h3 className="text-xl font-bold text-foreground !m-0 leading-none">{paymentPlanData.name}</h3>
                            <div className="flex items-baseline">
                                <span className="text-3xl font-bold text-foreground">$0</span>
                            </div>
                            <p className="text-xs font-medium text-[#53BDEB] uppercase tracking-wide mt-0.5">{t('pricing.freeForever')}</p>
                        </div>
                    </div>

                    {/* Middle: Features */}
                    <div
                        className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-center gap-x-8 gap-y-3 flex-1 bg-muted/30 rounded-lg p-4 mx-4"
                    >
                        {paymentPlanData.benefits.map((benefit, idx) => (
                            <div key={`benefit-${idx}`} className="flex items-center gap-2.5">
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-foreground">{benefit}</span>
                            </div>
                        ))}
                        {paymentPlanData.limitations.map((limitation, idx) => (
                            <div key={`limitation-${idx}`} className="flex items-center gap-2.5 opacity-75">
                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-muted-foreground">{limitation}</span>
                            </div>
                        ))}
                    </div>

                    {/* Right: CTA */}
                    {isCurrentPlan ? (
                        <div className="flex-shrink-0">
                            <ChargebeeButton
                                plan={paymentPlanData.planKey}
                                duration="Monthly"
                                label={t('planSelection.currentPlan')}
                                disabled={true}
                                seats={seatCount}
                                variant="regular"
                                size="lg"
                                className="w-full sm:w-auto cursor-default opacity-100"
                            />
                        </div>
                    ) : shouldShowFreeTileCancelCta({
                        isCurrentPlan,
                        hasPaidSubscription,
                        activeStatus: activeSubscription?.status,
                    }) ? (
                        // FE#194 — an active PAID Chargebee subscriber can now cancel from here.
                        // plan="Free" routes ChargebeeButton down its cancel branch
                        // (confirm-then-cancel + ask-why → chargebee.cancelActiveSubscription).
                        <div className="flex-shrink-0" data-testid="cancel-subscription-cta">
                            <ChargebeeButton
                                plan="Free"
                                duration="Monthly"
                                label={t('planSelection.cancelSubscription', { defaultValue: 'Cancel Subscription' })}
                                disabled={false}
                                seats={seatCount}
                                variant="outline"
                                size="lg"
                                className="w-full sm:w-auto border-muted-foreground/20 text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            />
                        </div>
                    ) : null}
                </div>

            );
        }

        // Calculate actual savings based on pricing data
        const isAnnual = planDuration === 'year';
        const monthlyPrice = paymentPlanData.price;

        // Get the monthly billing price for comparison (if we're on annual)
        const monthlyBillingPrice = isAnnual && planMap ? (() => {
            const monthlyPlan = Object.values(planMap).find(p =>
                p.planType === paymentPlanData.key &&
                p.intervalUnit === 'month' &&
                p.seatCount === seatCount
            );
            if (!monthlyPlan) return null;

            // Find the monthly price from itemPricesData
            const planTypeMap = {
                'Free': 'Free',
                'Scheduler': 'Basic',
                'Manager': 'StandardNew',
                'Campaigner': 'Pro'
            };
            const chargebeePlanType = planTypeMap[paymentPlanData.key as keyof typeof planTypeMap];
            const monthlyChargebeePlan = ((itemPricesData as unknown) as Array<{
                id: string;
                tiers: Array<{ starting_unit: number; ending_unit?: number; price: number; }>;
            }>)?.find(price => {
                const [planType] = price.id.split('-');
                return planType === chargebeePlanType && price.id.includes('Monthly');
            });

            const tier = monthlyChargebeePlan?.tiers?.find(tier =>
                seatCount >= tier.starting_unit &&
                (!tier.ending_unit || seatCount <= tier.ending_unit)
            );

            return tier ? Number((tier.price / 100).toFixed(1)) : null;
        })() : null;

        const showAnnualDiscount = isAnnual && monthlyPrice && monthlyBillingPrice && monthlyBillingPrice > monthlyPrice;
        const savingsAmount = monthlyBillingPrice && monthlyPrice ? monthlyBillingPrice - monthlyPrice : 0;
        const savingsPercent = monthlyBillingPrice && monthlyPrice ? Math.round((savingsAmount / monthlyBillingPrice) * 100) : 0;

        return (
            <div
                key={index}
                className={cn(
                    "relative rounded-xl shadow-lg border overflow-visible transition-all duration-300 flex flex-col bg-card dark:bg-[#1e293b]",
                    isHero
                        ? "border-2 border-[#53BDEB] shadow-[0_8px_30px_rgba(83,189,235,0.3)] scale-105 z-10"
                        : "border-gray-200 dark:border-[#334155] hover:-translate-y-1 hover:shadow-xl"
                )}
                data-testid={`subscription-plan-${paymentPlanData.name}`}
            >
                {/* Current Plan Badge */}
                {isCurrentPlan && (
                    <div className="absolute top-0 left-0 right-0 flex justify-center z-20">
                        <div className="bg-blue-600 text-white px-4 py-1.5 shadow-lg text-xs font-bold uppercase tracking-wider border-2 border-background rounded-full flex items-center gap-1.5 transform -translate-y-1/2">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t('planSelection.currentPlan')}
                        </div>
                    </div>
                )}

                {/* Most Popular Badge - Only show if NOT current plan AND no paid sub */}
                {showMostPopularBadge && (
                    <div className="absolute top-0 left-0 right-0 flex justify-center z-10">
                        <div className="bg-blue-600 text-white px-4 py-1.5 shadow-lg text-xs font-bold uppercase tracking-wider border-2 border-white rounded-full flex items-center gap-1.5 transform -translate-y-1/2">
                            <TrendingUp className="w-3.5 h-3.5" />
                            {t('planSelection.mostPopular')}
                        </div>
                    </div>
                )}

                {/* Header with gradient background */}
                <div
                    className={cn(
                        "pt-6 px-6 pb-4 text-center rounded-t-[10px]",
                        isHero
                            ? "bg-gradient-to-r from-[#259ae9] to-[#53BDEB] text-white"
                            : "bg-gray-50 dark:bg-[#0f172a] text-foreground border-b border-gray-200 dark:border-[#334155]"
                    )}
                >
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <PlanIcon
                            className={cn(
                                "w-6 h-6",
                                isHero ? "text-white" : "text-[#53BDEB]"
                            )}
                        />
                        <h2
                            className={cn(
                                "text-2xl font-bold",
                                isHero ? "text-white" : "text-foreground"
                            )}
                        >{paymentPlanData.name}</h2>
                    </div>
                    {paymentPlanData.enterprise && (
                        <p className="text-sm text-muted-foreground">
                            Custom solution for large teams
                        </p>
                    )}
                </div>

                {/* Pricing Section */}
                {!paymentPlanData.enterprise && (
                    <div
                        className={cn(
                            "px-6 pb-6 pt-6 flex flex-col flex-1 rounded-b-[10px]",
                            isHero
                                ? "bg-gradient-to-b from-[#53BDEB]/10 to-card dark:to-[#1e293b]"
                                : "bg-card dark:bg-[#1e293b]"
                        )}
                    >  <div className="flex items-baseline justify-center gap-2 mb-2">
                            <span className="text-4xl font-bold text-foreground">
                                ${paymentPlanData.key === 'Free' ? '0' : monthlyPrice}
                            </span>
                            <span className="text-muted-foreground">{t('pricing.perUserMonth', '/user/month')}</span>
                        </div>

                        {paymentPlanData.key === 'Free' && (
                            <p className="text-sm mt-2 text-muted-foreground">
                                {t('pricing.freeForever')}
                            </p>
                        )}

                        {showAnnualDiscount && monthlyBillingPrice && (
                            <div className="flex items-center justify-center gap-2 mt-2">
                                <span className="text-sm text-gray-500 line-through">
                                    ${monthlyBillingPrice}{t('pricing.perMonth', '/month')}
                                </span>
                                <Badge className="bg-green-600 text-white border-green-700 font-semibold">
                                    {t('pricing.saveAmount', 'Save {{amount}}/mo', { amount: `$${savingsAmount.toFixed(1)}` })}
                                </Badge>
                            </div>
                        )}

                        {showAnnualDiscount && (
                            <p className="text-xs mt-2 text-muted-foreground">
                                <strong className="text-foreground">${(monthlyPrice * 12).toFixed(0)}</strong> {t('pricing.billedAnnually', 'billed annually')}
                            </p>
                        )}


                        {/* Features List */}
                        <div className="space-y-3 mb-6 flex-1">
                            {paymentPlanData.benefits.map((benefit, idx) => {
                                const isOfflineFeature = benefit.includes(t(entitlementDescriptionMap["offline-socket"]));
                                return (
                                    <div key={`benefit-${idx}`} className="flex items-start gap-3 group" title={isOfflineFeature ? "Send your scheduled messages even when your computer is off!" : ""}>
                                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-foreground">{benefit}</p>
                                        </div>
                                    </div>
                                );
                            })}
                            {paymentPlanData.limitations.map((limitation, idx) => (
                                <div key={`limitation-${idx}`} className="flex items-start gap-3 opacity-50">
                                    <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">{limitation}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* CTA Button - Pushed to bottom */}
                        <div className="flex flex-col items-center gap-3 mt-auto">
                            {/* Active Plan Indicator Removed - Top Badge is sufficient */}
                            {user ? (
                                <>
                                    {!paymentPlanData.enterprise && paymentPlanData.key !== "Free" ? (
                                        isLoadingPrices ? (
                                            <div className="flex justify-center py-4">
                                                <CircularProgress size={24} />
                                            </div>
                                        ) : (
                                            <div data-testid={`subscription-upgrade-button-${paymentPlanData.name}`} className="flex justify-center w-full">
                                                <ChargebeeButton
                                                    plan={paymentPlanData.planKey}
                                                    duration={paymentPlanData.period === 'month' ? "Monthly" : "Yearly"}
                                                    label={CTA}
                                                    disabled={isCurrentPlan && activeSubscription?.status === 'active'}
                                                    seats={seatCount}
                                                    variant={buttonType === ButtonType.Downgrade ? 'outline' : 'regular'}
                                                    size="lg"
                                                    className={buttonType === ButtonType.Downgrade ? "w-full border-muted-foreground/20 text-muted-foreground hover:bg-muted/50 hover:text-foreground" : "w-full"}
                                                    couponCode={urlCouponCode}
                                                />
                                            </div>
                                        )
                                    ) : paymentPlanData.enterprise ? (
                                        <Button
                                            asChild
                                            size="lg"
                                            className="w-full bg-gradient-to-r from-[#259ae9] to-[#53BDEB] hover:from-[#2089d1] hover:to-[#45acd9] text-white font-semibold"
                                        >
                                            <a href="https://tudoboard.com/contact">
                                                {t('planSelection.contactSales')}
                                            </a>
                                        </Button>
                                    ) : null}
                                </>
                            ) : (
                                <Button
                                    asChild
                                    size="lg"
                                    className="w-full bg-gray-800 hover:bg-gray-700 text-white"
                                >
                                    <a href="/login?redirect=/account-subscription">
                                        {t('planSelection.loginToBuy')}
                                    </a>
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Enterprise Section */}
                {paymentPlanData.enterprise && (
                    <div className="p-8 text-center flex flex-col flex-1 justify-center">
                        <img src={Enterprise} alt={t('planSelection.enterprise')} className="h-20 mx-auto mb-6" />
                        <Button
                            asChild
                            size="lg"
                            className="w-full bg-gradient-to-r from-[#259ae9] to-[#53BDEB] hover:from-[#2089d1] hover:to-[#45acd9] text-white font-semibold"
                        >
                            <a href="https://tudoboard.com/contact">
                                {t('planSelection.contactSales')}
                            </a>
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    console.log('isGettingPaymentUrl', isGettingPaymentUrl);

    return (
        <div className="w-full max-w-7xl mx-auto py-6">
            {(isLoadingPrices || isLoadingPlanMap || isGettingPaymentUrl) ? (
                <div className="flex items-center justify-center min-h-[80vh] flex-col gap-5">
                    <CircularProgress />
                    <p className="text-muted-foreground">Loading pricing options...</p>
                </div>
            ) : (
                <>
                    {/* Controls Section */}
                    <div
                        className="flex flex-col sm:flex-row gap-6 items-center justify-center mb-12 p-6 rounded-xl shadow-sm max-w-3xl mx-auto border"
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            borderColor: 'var(--color-border)'
                        }}
                    >
                        {/* Team Size Selector */}
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <label className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                                <Users className="w-4 h-4" />
                                {t('planSelection.teamSize')}
                            </label>
                            <div className="relative">
                                <select
                                    value={seatCount}
                                    onChange={(e) => setSeatCount(Number(e.target.value))}
                                    className="w-full sm:w-32 appearance-none rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors border"
                                    style={{
                                        backgroundColor: 'var(--color-bg)',
                                        borderColor: 'var(--color-border)',
                                        color: 'var(--color-text)'
                                    }}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50].map((num) => (
                                        <option key={num} value={num}>
                                            {num} {num === 1 ? 'user' : 'users'}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" style={{ color: 'var(--color-text)' }} />
                            </div>
                        </div>

                        {/* Billing Period Toggle */}
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                            <label className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>
                                <Calendar className="w-4 h-4" />
                                {t('planSelection.billingPeriod')}
                            </label>
                            <div className="flex items-center p-1 rounded-lg border" style={{
                                backgroundColor: 'var(--color-bg)',
                                borderColor: 'var(--color-border)'
                            }}>
                                <button
                                    onClick={() => setPlanDuration('year')}
                                    className={cn(
                                        "flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200",
                                        planDuration === 'year' ? "shadow-sm" : ""
                                    )}
                                    style={{
                                        backgroundColor: planDuration === 'year' ? 'var(--color-surface)' : 'transparent',
                                        color: planDuration === 'year' ? 'var(--color-text)' : 'var(--color-text-muted)'
                                    }}
                                >
                                    {t('planSelection.annually')}
                                    {planDuration === 'year' && (
                                        <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                                            -20%
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => {
                                        analyticsService.event(`set_billing_period_monthly`, {
                                            category: "buySubscriptionView",
                                            action: `set billing period monthly`,
                                        });
                                        setPlanDuration('month');
                                    }}
                                    className={cn(
                                        "px-3 py-1.5 rounded-md text-sm font-semibold transition-all",
                                        planDuration === 'month' ? "shadow-sm" : ""
                                    )}
                                    style={{
                                        backgroundColor: planDuration === 'month' ? 'var(--color-surface)' : 'transparent',
                                        color: planDuration === 'month' ? 'var(--color-text)' : 'var(--color-text-muted)'
                                    }}
                                >
                                    {t('planSelection.monthly')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Plans Grid - Paid Plans Only */}
                    {activePlanMap ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                                {planDescription?.filter(plan => plan.key !== 'Free').map((plan, index) => subscriptionBox(index, plan))}
                            </div>

                            {/* Free Plan - Full Width at Bottom */}
                            {planDescription?.filter(plan => plan.key === 'Free').map((plan, index) => (
                                <div key="free-plan" className="mb-8">
                                    {subscriptionBox(index, plan, true)}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className="flex justify-center py-12">
                            <CircularProgress />
                        </div>
                    )}

                </>
            )}
        </div>
    );
}

export default PlanSelection;